/**
 * localStorage key migration: aesthetix_* -> helios_*
 *
 * Run once on app startup. Reads old keys, writes to new keys,
 * removes old keys. Idempotent: safe to run multiple times.
 */

const KEY_MAP: Record<string, string> = {
  // Settings
  'aesthetix_units': 'helios_units',
  'aesthetix_theme_pref': 'helios_theme_pref',
  'aesthetix_theme': 'helios_theme',
  'aesthetix_haptics_enabled': 'helios_haptics_enabled',
  'aesthetix_sound_enabled': 'helios_sound_enabled',
  'aesthetix_sound_volume': 'helios_sound_volume',
  'aesthetix_reduced_motion': 'helios_reduced_motion',
  'aesthetix_sidebar_collapsed': 'helios_sidebar_collapsed',
  'aesthetix_quotes_hidden': 'helios_quotes_hidden',

  // UI state
  'aesthetix_guidelines_seen': 'helios_guidelines_seen',
  'aesthetix_custom_draft': 'helios_custom_draft',
  'aesthetix_changelog_seen': 'helios_changelog_seen',
  'aesthetix_tour_complete': 'helios_tour_complete',
  'aesthetix_founder_welcomed': 'helios_founder_welcomed',
  'aesthetix_photo_tips_hidden': 'helios_photo_tips_hidden',
  'aesthetix_quality_seen': 'helios_quality_seen',
  'aesthetix_benchmark_mode': 'helios_benchmark_mode',
  'aesthetix_show_position': 'helios_show_position',
  'aesthetix_onboarding_done': 'helios_onboarding_done',
  'aesthetix_feedback_considered_banner_shown': 'helios_feedback_considered_banner_shown',
  'aesthetix_reported_posts': 'helios_reported_posts',

  // Training
  'aesthetix_difficulty_filter': 'helios_difficulty_filter',
  'aesthetix_program_filter': 'helios_program_filter',
  'aesthetix_1rm': 'helios_1rm',
  'aesthetix_sport_mode': 'helios_sport_mode',
  'aesthetix_mood': 'helios_mood',

  // Data
  'aesthetix_water': 'helios_water',
  'aesthetix_goal_weight': 'helios_goal_weight',
  'aesthetix_pending_referral_code': 'helios_pending_referral_code',

  // Skill tree
  'aesthetix-skilltree-v2': 'helios-skilltree-v2',

  // Sounds
  'aesthetix_sounds': 'helios_sounds',
  'aesthetix-haptics-enabled': 'helios-haptics-enabled',
};

export function migrateLocalStorageKeys(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    // Skip if already migrated
    if (localStorage.getItem('helios_migration_done') === '1') return;

    let migrated = 0;
    for (const [oldKey, newKey] of Object.entries(KEY_MAP)) {
      const value = localStorage.getItem(oldKey);
      if (value !== null && localStorage.getItem(newKey) === null) {
        localStorage.setItem(newKey, value);
        localStorage.removeItem(oldKey);
        migrated++;
      }
    }

    // Also migrate dynamic keys (with dates/IDs in them)
    const dynamicPrefixes = [
      'aesthetix_day_done_', 'aesthetix_freezes_', 'aesthetix_freeze_active',
      'aesthetix_streak_toasts', 'aesthetix_streak_milestone_',
      'aesthetix_recovery_trend_', 'aesthetix_week_complete_',
      'aesthetix_easter_month_', 'aesthetix_jump_',
      'aesthetix_nut_ack_', 'aesthetix_cn_dismissed',
      'aesthetix_deload_dismissed', 'aesthetix_high_week_banner_dismissed',
      'aesthetix_bridge_dismissed', 'aesthetix_coach_daily',
      'aesthetix_coach_advice_', 'aesthetix_recognition_sent_',
      'aesthetix_session_feeling_', 'aesthetix_nutrition_rotation_',
      'aesthetix_nutrition_ai_',
    ];

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;
      for (const prefix of dynamicPrefixes) {
        if (key.startsWith(prefix)) {
          const newKey = key.replace('aesthetix_', 'helios_').replace('aesthetix-', 'helios-');
          const value = localStorage.getItem(key);
          if (value !== null && localStorage.getItem(newKey) === null) {
            localStorage.setItem(newKey, value);
            localStorage.removeItem(key);
            migrated++;
          }
          break;
        }
      }
    }

    // Session storage too
    const sessionPrefixes = [
      'aesthetix-training-plan', 'aesthetix-training-plan-time',
      'aesthetix-training-stale-dismissed', 'aesthetix-training-new-analysis-dismissed',
      'aesthetix-analysis-loaded', 'aesthetix-daily-analysis',
    ];
    for (const oldKey of sessionPrefixes) {
      const newKey = oldKey.replace('aesthetix-', 'helios-');
      const value = sessionStorage.getItem(oldKey);
      if (value !== null && sessionStorage.getItem(newKey) === null) {
        sessionStorage.setItem(newKey, value);
        sessionStorage.removeItem(oldKey);
      }
    }

    localStorage.setItem('helios_migration_done', '1');
    if (migrated > 0) {
      console.log(`[HELIOS] Migrated ${migrated} localStorage keys from aesthetix_* to helios_*`);
    }
  } catch {
    // localStorage may be unavailable (private browsing, storage full)
  }
}
